/**
 * 邀请码管理（邀约准入制的入口）。
 *
 * 这个页面存在的理由很直接：成员注册**只有一条路径** ——
 * C 端 `POST /api/auth/otp/verify` + 有效邀请码。而邀请码此前只能由 `db/seed.ts` 写入，
 * 生产环境演示数据被关闭后 `invite_codes` 为 0 行，结果是「没有任何人能注册」。
 * 发码能力必须由界面提供，而不是靠跑一次 seed。
 *
 * 三条口径：
 *   1. **计数与状态全部来自接口**（已用 / 上限 / 剩余 / 状态）。界面不自己算 ——
 *      尤其不能用 `usedCount >= maxUses` 推断「已用完」：作废的实现也是把两者置为相等，
 *      自作聪明会把一次人工撤销显示成自然耗尽；
 *   2. 无权限（403）时**如实显示服务端原文**，不伪装成空列表 ——
 *      「看不到」与「没有邀请码」是完全不同的两个结论，后者会让人去重复发码；
 *   3. 作废是**不可逆**的（码立即失效，而码通常已经发给真人了），因此要二次确认。
 */
import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api.ts';
import type { InviteItem, InvitesResult } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { formatDateTime, formatText } from '../lib/format.ts';

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

/** 状态徽章色调：只有「可用」配绿色；「已作废」是人工撤销，必须显眼。 */
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'ghost'> = {
  available: 'ok',
  exhausted: 'warn',
  revoked: 'danger',
  expired: 'ghost',
};

function CreateInviteForm({ levels, onDone }: { levels: InvitesResult['levels']; onDone: (toast: Toast) => void }) {
  const [level, setLevel] = useState(levels[0]?.key ?? '');
  const [referrer, setReferrer] = useState('');
  const [note, setNote] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [expiresAt, setExpiresAt] = useState('');
  const [count, setCount] = useState('1');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.createInvites({
        level,
        ...(referrer.trim() ? { referrer: referrer.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        maxUses: Number(maxUses) || 1,
        ...(expiresAt ? { expiresAt } : {}),
        count: Number(count) || 1,
        ...(code.trim() ? { code: code.trim().toUpperCase() } : {}),
      });
      const codes = result.items.map((i) => i.code);
      onDone({
        text:
          `已生成 ${result.created} 个邀请码：${codes.slice(0, 3).join('、')}${codes.length > 3 ? ` 等 ${codes.length} 个` : ''}` +
          `（${result.items[0]?.levelName ?? level} · 每个可用 ${result.items[0]?.maxUses ?? maxUses} 次）` +
          (result.warning ? ` ⚠ ${result.warning}` : ''),
        tone: 'ok',
      });
    } catch (err) {
      // 服务端原文照实呈现：400/409 都带着「为什么」的说明，不要在界面上把它抹成一句「提交失败」
      setError(err instanceof ApiError ? `创建被拒绝（${err.status}）：${err.message}` : '创建请求失败，请确认后端服务可用');
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">新建邀请码</div>
      <div className="dp-s">
        邀请码是 HEW³ 唯一的注册入口：成员在 C 端用手机验证码 + 邀请码开户，会籍级别取自邀请码。
        因此这里的「会籍级别」决定了对方以什么身份加入，不是备注。
      </div>
      <div className="grid2">
        <div className="field">
          <label className="field-label" htmlFor="inv-level">
            会籍级别（取值由服务端下发）
          </label>
          <select id="inv-level" className="field-input" value={level} onChange={(e) => setLevel(e.target.value)}>
            {levels.map((l) => (
              <option key={l.key} value={l.key}>
                {l.name}（{l.key}）
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="inv-referrer">
            推荐来源（可选，会写入成员的推荐关系）
          </label>
          <input
            id="inv-referrer"
            className="field-input"
            placeholder="例如：政商战略智库 · 推荐"
            value={referrer}
            maxLength={60}
            onChange={(e) => setReferrer(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="inv-max">
            每个码可用次数
          </label>
          <input
            id="inv-max"
            className="field-input"
            type="number"
            min={1}
            max={1000}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="inv-expires">
            过期时间（可选；只填日期即当天 23:59:59 失效）
          </label>
          <input
            id="inv-expires"
            className="field-input"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="inv-count">
            生成数量（1–50）
          </label>
          <input
            id="inv-count"
            className="field-input"
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="inv-code">
            自定义编码（可选；不填则自动生成且保证不重复）
          </label>
          <input
            id="inv-code"
            className="field-input"
            placeholder="HEW3-XXXXXXXX"
            value={code}
            maxLength={32}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="inv-note">
          备注（可选，例如发给谁、什么场合）
        </label>
        <input
          id="inv-note"
          className="field-input"
          placeholder="例如：2026 春季私董会现场发放"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy || !level} onClick={() => void submit()}>
          {busy ? '正在生成…' : '生成邀请码'}
        </button>
        <span className="text-tiny muted">生成动作会写入审计链（含工号），因此「谁发的码」可追溯。</span>
      </div>
    </div>
  );
}

export function InvitesPage() {
  const [toast, setToast] = useState<Toast | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad((signal) => adminApi.invites(signal));

  if (loading) return <LoadingBox text="正在加载邀请码…" />;
  // 403 时把服务端原文摆出来：「无权限」绝不能被渲染成「一个邀请码都没有」
  if (error) return <ErrorBox message={errorText(error)} onRetry={reload} />;
  if (!data) return <ErrorBox message="邀请码列表为空" onRetry={reload} />;

  const items = data.items;

  const handleDone = (result: Toast): void => {
    setToast(result);
    reload();
    window.setTimeout(() => setToast(null), 5000);
  };

  const revoke = async (item: InviteItem): Promise<void> => {
    // 二次确认：作废不可撤销，而码通常已经发给真人了
    const confirmed = window.confirm(
      `确认作废邀请码「${item.code}」？\n\n` +
        `已用 ${item.usedCount}/${item.maxUses} · 剩余 ${item.remaining}\n` +
        '作废后该码立即失效（剩余次数归零），已用它注册的成员不受影响。',
    );
    if (!confirmed) return;
    setRevokingId(item.id);
    try {
      const result = await adminApi.revokeInvite(item.id);
      handleDone({ text: `已作废「${result.item.code}」（剩余 ${result.item.remaining} 次 · ${result.item.statusLabel}）`, tone: 'ok' });
    } catch (err) {
      handleDone({
        text: err instanceof ApiError ? `作废被拒绝（${err.status}）：${err.message}` : '作废请求失败，请确认后端服务可用',
        tone: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div>
      <PageHead
        title="邀请码管理"
        sub="成员注册的唯一入口。状态与计数全部来自数据库；作废是不可逆操作，界面上会二次确认。"
        actions={
          <span className="badge ghost">
            共 {data.summary.total} 个码 · 可用 {data.summary.available} 个
          </span>
        }
      />

      <Card title="发放状态" sub="计数来自 /api/admin/invites 的 summary，界面不写死任何数字">
        <div className="grid4">
          <Kpi label="可用" value={data.summary.available} tone="e" delta="未过期且未用尽" />
          <Kpi label="已用完" value={data.summary.exhausted} tone="warn" delta="已用次数达到上限" />
          <Kpi label="已过期" value={data.summary.expired} delta="过了有效期，注册会被拒" />
          <Kpi label="已作废" value={data.summary.revoked} tone="danger" delta="人工撤销，剩余次数已归零" />
        </div>
      </Card>

      <Card tight>
        <CreateInviteForm levels={data.levels} onDone={handleDone} />
      </Card>

      <Card tight>
        {items.length === 0 && (
          <EmptyBox
            title="当前没有任何邀请码"
            desc="这不是「没有数据可显示」，而是确实一个码都没有 —— 此时**任何人都无法注册成为成员**。请用上方表单生成第一批邀请码。"
          />
        )}

        {items.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>邀请码</th>
                  <th>会籍级别</th>
                  <th>推荐来源</th>
                  <th>备注</th>
                  <th style={{ textAlign: 'right' }}>已用 / 上限</th>
                  <th style={{ textAlign: 'right' }}>剩余</th>
                  <th>过期时间</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono-num">
                      <b>{item.code}</b>
                    </td>
                    <td>{item.levelName}</td>
                    <td>{formatText(item.referrer)}</td>
                    <td>{formatText(item.note)}</td>
                    <td className="num-cell">
                      {item.usedCount} / {item.maxUses}
                    </td>
                    <td className="num-cell">{item.remaining}</td>
                    <td className="mono-num">{item.expiresAt ? formatDateTime(item.expiresAt) : '长期有效'}</td>
                    <td>
                      <Badge tone={STATUS_TONE[item.status] ?? 'ghost'}>{item.statusLabel}</Badge>
                      {item.revokedAt !== null && (
                        <div className="muted text-tiny">作废于 {formatDateTime(item.revokedAt)}</div>
                      )}
                    </td>
                    <td className="mono-num">{formatDateTime(item.createdAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {item.revoked ? (
                        <span className="muted text-tiny">已作废</span>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          disabled={revokingId === item.id}
                          onClick={() => void revoke(item)}
                        >
                          {revokingId === item.id ? '正在作废…' : '作废'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>状态口径</b>
        <br />
        可用 = 未作废、未过期、已用次数未达上限；已用完 = 已用次数达到上限；已过期 = 当前时间已过
        <span className="mono-num"> expires_at</span>；已作废 = 被员工人工撤销（剩余次数一并归零）。
        「已用完」与「已作废」在数据库里同形，靠作废时间字段区分 —— 因此状态一律以接口返回的
        <span className="mono-num"> status / statusLabel</span> 为准，界面不得自行推断。
      </div>

      <div className="note-src">
        <b>作废为什么是「置为已用满」而不是删除</b>
        <br />
        已用过的码要留下来：否则「谁是用哪个码进来的」就断了线索。因此作废只把上限压到已用次数
        （剩余归零、数据自洽），记录本身保留，并写入审计链（含工号与作废时间）。
        {items.length === 0 && ' 列表为空说明当前一个码都没有，而不是「筛选后没有」。'}
        {items.some((i) => i.expiresAt === null) && ' 未设置过期时间的码为长期有效，请定期复核。'}
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
      <div className="sp8" />
    </div>
  );
}
